// Basic CRUD operations for Firestore
// This module contains all basic Create, Read, Update, Delete operations

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  DocumentData,
} from "firebase/firestore";
import { db } from "../../../utils/firebase";
import { OutreachCategory } from '../../../types/outreach';
import { OUTREACH_DOC_PATH } from '../utils/constants';
import { convertDoc } from '../utils/helpers';

interface OutreachCrudOptions {
  suppressErrors?: boolean;
}

/**
 * Get all items from a specific category
 *
 * @param category Category to get items from
 * @returns Array of items
 */
export const getOutreachItems = async <T>(
  category: OutreachCategory,
  options: OutreachCrudOptions = {}
): Promise<T[]> => {
  const { suppressErrors = true } = options;
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastAccessed: new Date() }, { merge: true });

    const collectionRef = collection(db, `${OUTREACH_DOC_PATH}/${category}`);
    const snapshot = await getDocs(collectionRef);
    return snapshot.docs.map((doc) => convertDoc<T>(doc));
  } catch (error) {
    console.error(`Error getting ${category} items:`, error);
    if (!suppressErrors) {
      throw error instanceof Error
        ? error
        : new Error(`Failed to get ${category} items`);
    }
    return [];
  }
};

/**
 * Get a single item by ID
 *
 * @param category Category the item belongs to
 * @param id ID of the item to get
 * @returns The item or null if not found
 */
export const getOutreachItem = async <T>(
  category: OutreachCategory,
  id: string
): Promise<T | null> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastAccessed: new Date() }, { merge: true });

    const docRef = doc(db, `${OUTREACH_DOC_PATH}/${category}`, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as T;
    }

    return null;
  } catch (error) {
    console.error(`Error getting ${category} item ${id}:`, error);
    return null;
  }
};

/**
 * Add a new item
 *
 * @param category Category to add the item to
 * @param item Item to add
 * @returns ID of the newly created item
 */
export const addOutreachItem = async <T>(
  category: OutreachCategory,
  item: Omit<T, "id">
): Promise<string> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastModified: new Date() }, { merge: true });

    const collectionRef = collection(db, `${OUTREACH_DOC_PATH}/${category}`);
    const docRef = await addDoc(collectionRef, item);
    return docRef.id;
  } catch (error) {
    console.error(`Error adding ${category} item:`, error);
    throw error;
  }
};

/**
 * Update an existing item
 *
 * @param category Category the item belongs to
 * @param id ID of the item to update
 * @param item Partial item data to update
 */
export const updateOutreachItem = async <T>(
  category: OutreachCategory,
  id: string,
  item: Partial<T>
): Promise<void> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastModified: new Date() }, { merge: true });

    const docRef = doc(db, `${OUTREACH_DOC_PATH}/${category}`, id);
    await updateDoc(docRef, item as DocumentData);
  } catch (error) {
    console.error(`Error updating ${category} item ${id}:`, error);
    throw error;
  }
};

/**
 * Delete an item
 *
 * @param category Category the item belongs to
 * @param id ID of the item to delete
 */
export const deleteOutreachItem = async (
  category: OutreachCategory,
  id: string
): Promise<void> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastModified: new Date() }, { merge: true });

    const docRef = doc(db, `${OUTREACH_DOC_PATH}/${category}`, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error(`Error deleting ${category} item ${id}:`, error);
    throw error;
  }
};

/**
 * Delete all items in a category
 *
 * @param category Category to delete all items from
 */
export const deleteAllOutreachItems = async (
  category: OutreachCategory
): Promise<void> => {
  try {
    console.log(`Deleting all items in ${category}...`);
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastModified: new Date() }, { merge: true });

    const collectionRef = collection(db, `${OUTREACH_DOC_PATH}/${category}`);
    const snapshot = await getDocs(collectionRef);

    // Delete each document in the collection
    const deletePromises = snapshot.docs.map((doc) => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    console.log(`Successfully deleted all items in ${category}`);
  } catch (error) {
    console.error(`Error deleting all items in ${category}:`, error);
    throw error;
  }
};
